// src/theme/theme.ts
import { createTheme, Theme } from '@mui/material/styles';
import { decoratedTextFieldRecipeStyles } from './recipes/forms';
import { colorPrimitives } from './tokens/primitives';
import { semanticTokens } from './tokens/semantic';

const colors = colorPrimitives;

// Función para crear el tema base de la aplicación
export const createAppTheme = (): Theme => {
  return createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: colors.primary[600],
        light: colors.primary[400],
        dark: colors.primary[800],
        contrastText: '#ffffff',
      },
      secondary: {
        main: colors.secondary[600],
        light: colors.secondary[400],
        dark: colors.secondary[800],
        contrastText: '#ffffff',
      },
      success: {
        main: colors.success[600],
        light: colors.success[400],
        dark: colors.success[800],
      },
      warning: {
        main: colors.warning[600],
        light: colors.warning[400],
        dark: colors.warning[800],
      },
      error: {
        main: colors.error[600],
        light: colors.error[400],
        dark: colors.error[800],
      },
      background: {
        default: semanticTokens.surface.page,
        paper: semanticTokens.surface.card,
      },
      text: {
        primary: semanticTokens.text.primary,
        secondary: semanticTokens.text.secondary,
      },
      divider: semanticTokens.border.subtle,
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontSize: '2.5rem',
        fontWeight: 700,
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 600,
        lineHeight: 1.3,
        letterSpacing: '-0.01em',
      },
      h3: {
        fontSize: '1.5rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h4: {
        fontSize: '1.25rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h5: {
        fontSize: '1.125rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.6,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.5,
      },
      button: {
        textTransform: 'none',
        fontWeight: 600,
      },
    },
    shape: {
      borderRadius: 12,
    },
    spacing: 8,
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: '10px 24px',
            fontSize: '0.875rem',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            },
          },
          contained: {
            '&:hover': {
              boxShadow: '0 6px 20px rgba(0, 0, 0, 0.2)',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            border: `1px solid ${colors.grey[200]}`,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              ...decoratedTextFieldRecipeStyles['& .MuiOutlinedInput-root'],
            },
            '& .MuiInputBase-input': decoratedTextFieldRecipeStyles['& .MuiInputBase-input'],
            '& .MuiInputBase-input:-webkit-autofill':
              decoratedTextFieldRecipeStyles['& .MuiInputBase-input:-webkit-autofill'],
            '& .MuiInputLabel-root': decoratedTextFieldRecipeStyles['& .MuiInputLabel-root'],
            '& .MuiInputLabel-root.Mui-focused':
              decoratedTextFieldRecipeStyles['& .MuiInputLabel-root.Mui-focused'],
            '& .MuiFormHelperText-root':
              decoratedTextFieldRecipeStyles['& .MuiFormHelperText-root'],
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            backgroundColor: '#ffffff',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${colors.grey[200]}`,
          },
        },
      },
    },
  });
};

// Tema por defecto (light)
const theme = createAppTheme();

export default theme;
export { colors };
